/**
 * Lobby wallet — left edge tab + slide-out panel so tables own the main screen.
 * Mobile: full-height drawer from left, large tap targets.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useWalletState } from "@/contexts/wallet-context";
import {
  isLoggedIn,
  loginWithGrudgeId,
  openGrudgeWalletSite,
} from "@/lib/grudge-auth";
import { installUrl, type SolanaWalletId } from "@/lib/solana-wallets";
import { BuyGbuxModal } from "@/components/buy-gbux-modal";
import { formatPlayGbux } from "@shared/gbux-math";
import { GBUX } from "@shared/fleet";
import { BRAND } from "@shared/brand";
import {
  Coins,
  ExternalLink,
  Link2,
  LogIn,
  Wallet,
  CheckCircle2,
  AlertCircle,
  X,
  ChevronRight,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  isMobileBrowser,
  openDappInPhantomBrowser,
  isInPhantomInAppBrowser,
} from "@/lib/mobile-web3";

export function LobbyWalletDash({
  compact = false,
  onReadySit,
  onClose,
}: {
  compact?: boolean;
  onReadySit?: () => void;
  onClose?: () => void;
}) {
  const {
    wallet,
    connect,
    disconnect,
    linkToGrudgeAccount,
    loginGrudgeId,
    detectedWallets,
    rescanWallets,
    refreshBalances,
    openWalletPanel,
  } = useWalletState();
  const { toast } = useToast();
  const [buyOpen, setBuyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [moreWallets, setMoreWallets] = useState(false);
  const mobile = typeof window !== "undefined" && isMobileBrowser();

  useEffect(() => {
    rescanWallets();
    void refreshBalances();
  }, [rescanWallets, refreshBalances]);

  const playGbux = wallet.playGbux || wallet.gbuxBalance || wallet.accountGbux || 0;
  const sitReady = wallet.canSit && !!wallet.publicKey;
  const chain = Number(wallet.chainGbux || 0);
  const phantomAvail = detectedWallets.some(
    (w) => w.id === "phantom" && w.available,
  );

  const onConnect = async (id: SolanaWalletId) => {
    setBusy(true);
    try {
      if (id === "phantom" && mobile && !phantomAvail && !isInPhantomInAppBrowser()) {
        // Mobile best practice: open dApp inside Phantom in-app browser
        openDappInPhantomBrowser(
          `${window.location.origin}/lobby`,
        );
        return;
      }
      if (id === "phantom" && mobile && !phantomAvail) {
        await connect("phantom", { phantomMode: "google" });
      } else {
        await connect(id);
      }
      toast({ title: "Wallet connected", description: id });
      if (isLoggedIn()) {
        try {
          await linkToGrudgeAccount();
          toast({ title: "Linked to Grudge account" });
        } catch {
          /* optional */
        }
      }
      await refreshBalances();
      onReadySit?.();
      onClose?.();
    } catch (e) {
      if (e instanceof Error && e.message === "REDIRECT_IN_PROGRESS") {
        toast({
          title: "Continue in Phantom",
          description: "Return here after sign-in",
        });
        return;
      }
      toast({
        title: "Connect failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`lobby-wallet-dash space-y-3 ${compact ? "p-3" : "p-4"}`}
      data-testid="lobby-wallet-dash"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold text-[#fff8e7]">
          <Wallet className="h-4 w-4 text-amber-400 shrink-0" />
          <span className="font-[Cinzel,serif] tracking-wide text-sm">
            Play wallet
          </span>
        </div>
        {onClose && (
          <button
            type="button"
            className="lobby-wallet-close"
            aria-label="Close wallet"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Play balance hero */}
      <div className="lobby-wallet-balance">
        <div className="flex items-center gap-2 mb-1">
          <img
            src={BRAND.gbuxCoinSm}
            alt=""
            width={20}
            height={20}
            className="w-5 h-5 object-contain"
          />
          <span className="text-[10px] uppercase tracking-widest text-amber-100/55">
            Play GBUX · server ledger
          </span>
        </div>
        <div
          className="text-3xl font-black text-emerald-300 tabular-nums leading-none"
          data-testid="dash-play-gbux"
        >
          {formatPlayGbux(playGbux)}
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-400 leading-snug">
          Tables · slots · BJ debit this. Welcome 200 on first connect.
        </p>
        {(wallet.accountGbux > 0 || chain > 0) && (
          <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-zinc-500">
            {wallet.accountGbux > 0 && (
              <span>Bag {formatPlayGbux(wallet.accountGbux)}</span>
            )}
            {chain > 0 && <span>Chain {formatPlayGbux(chain)}</span>}
          </div>
        )}
      </div>

      {/* Connect — mobile-first: one primary CTA */}
      {!wallet.publicKey ? (
        <div className="space-y-2">
          <Button
            className="w-full h-12 bg-violet-700 hover:bg-violet-600 font-bold text-base"
            disabled={busy}
            onClick={() => void onConnect("phantom")}
          >
            <Wallet className="mr-2 h-5 w-5" />
            {mobile && !phantomAvail
              ? "Open in Phantom app"
              : "Connect Phantom"}
          </Button>
          {mobile && !phantomAvail && (
            <Button
              className="w-full h-11 bg-violet-900/50 border border-violet-500/40"
              disabled={busy}
              onClick={() => void connect("phantom", { phantomMode: "google" })}
            >
              Phantom via Google / Apple
            </Button>
          )}
          <Button
            variant="outline"
            className="w-full h-11 border-sky-500/40 text-sky-100"
            onClick={() => loginGrudgeId("/lobby")}
          >
            <LogIn className="mr-2 h-4 w-4" />
            Grudge ID · id.grudge-studio.com
          </Button>
          <button
            type="button"
            className="w-full text-center text-[11px] text-zinc-500 py-1"
            onClick={() => setMoreWallets((v) => !v)}
          >
            {moreWallets ? "Hide other wallets" : "Other wallets"}
          </button>
          {moreWallets && (
            <div className="grid grid-cols-2 gap-2">
              {detectedWallets
                .filter((w) => w.id !== "generic" && w.id !== "phantom")
                .slice(0, 4)
                .map((w) => (
                  <Button
                    key={w.id}
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    className="justify-start text-xs h-11"
                    onClick={() => {
                      if (!w.available) {
                        window.open(installUrl(w.id), "_blank");
                        return;
                      }
                      void onConnect(w.id);
                    }}
                  >
                    <span className="mr-1">{w.icon}</span>
                    {w.name}
                  </Button>
                ))}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-emerald-500/25 bg-emerald-950/25 p-3 text-xs space-y-2">
          <p className="font-mono truncate text-emerald-100 text-sm">
            {wallet.walletType || "Wallet"} · {wallet.publicKey.slice(0, 6)}…
            {wallet.publicKey.slice(-4)}
          </p>
          <p className="text-emerald-400 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Ready to sit
          </p>
          <div className="flex flex-wrap gap-2">
            {wallet.isAuthenticated && (
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-600 h-10 text-xs"
                onClick={async () => {
                  try {
                    await linkToGrudgeAccount();
                    toast({ title: "Linked to Grudge account" });
                    await refreshBalances();
                  } catch (e) {
                    toast({
                      title: "Link failed",
                      description:
                        e instanceof Error ? e.message : String(e),
                      variant: "destructive",
                    });
                  }
                }}
              >
                <Link2 className="mr-1 h-3 w-3" />
                Link Grudge ID
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-10 text-xs"
              onClick={() => void disconnect()}
            >
              Disconnect
            </Button>
          </div>
        </div>
      )}

      {!wallet.isAuthenticated && wallet.publicKey && (
        <Button
          variant="outline"
          className="w-full h-11 border-violet-500/35 text-violet-100"
          onClick={() => loginGrudgeId("/lobby")}
        >
          <LogIn className="mr-2 h-4 w-4" />
          Sign in Grudge ID
        </Button>
      )}

      <div className="flex flex-col gap-2 pt-1">
        <Button
          className="w-full h-12 bg-amber-600 hover:bg-amber-500 font-bold text-base"
          onClick={() => {
            if (!wallet.publicKey) {
              void onConnect("phantom");
              return;
            }
            setBuyOpen(true);
          }}
        >
          <Coins className="mr-2 h-5 w-5" />
          Fund play GBUX
        </Button>
        <Button
          variant="outline"
          className="w-full h-11 border-zinc-600"
          onClick={() => openWalletPanel("transfer")}
        >
          Transfer details
        </Button>
        <Button
          variant="ghost"
          className="w-full h-9 text-xs text-zinc-400"
          onClick={() => openGrudgeWalletSite()}
        >
          Fleet wallet site
          <ExternalLink className="ml-2 h-3 w-3 opacity-60" />
        </Button>
        <Button
          className="w-full h-11"
          variant={sitReady ? "default" : "secondary"}
          disabled={!sitReady}
          onClick={() => {
            onReadySit?.();
            onClose?.();
          }}
          data-testid="button-sit-gateway"
        >
          {sitReady ? "Browse tables →" : "Connect to sit"}
        </Button>
        {sitReady && playGbux < 40 && (
          <p className="text-[11px] text-amber-200/70 flex items-start gap-1">
            <AlertTriangleIcon />
            Low play GBUX for NL 1/2 min buy-in (40+).
          </p>
        )}
      </div>

      <p className="text-[10px] text-zinc-600 text-center">
        ${GBUX.rateUsd}/GBUX · mainnet
      </p>

      <BuyGbuxModal open={buyOpen} onOpenChange={setBuyOpen} />
    </div>
  );
}

function AlertTriangleIcon() {
  return <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />;
}

function StatusTile({
  ok,
  label,
  value,
}: {
  ok: boolean;
  label: string;
  value: string;
}) {
  return (
    <div
      className={`rounded-lg border px-2 py-2.5 text-center min-w-0 ${
        ok
          ? "border-emerald-500/30 bg-emerald-950/30"
          : "border-zinc-700/50 bg-black/30"
      }`}
    >
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div
        className={`text-xs font-bold truncate ${
          ok ? "text-emerald-300" : "text-zinc-400"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Left-edge tab + slide-out wallet panel. Tables stay full-width.
 */
export function LobbyWalletSidePanel({
  onReadySit,
  defaultOpen = false,
}: {
  onReadySit?: () => void;
  /** Desktop only first-visit open — mobile stays tables-first */
  defaultOpen?: boolean;
}) {
  const { wallet, openWalletPanel } = useWalletState();
  const play = wallet.playGbux || wallet.gbuxBalance || 0;
  const sitReady = wallet.canSit && !!wallet.publicKey;
  const mobile =
    typeof window !== "undefined" &&
    (window.innerWidth < 768 || /Mobile|Android|iPhone/i.test(navigator.userAgent));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Never auto-cover the lobby on phones
    if (defaultOpen && !mobile) setOpen(true);
  }, [defaultOpen, mobile]);

  // Bottom-nav Wallet tab opens this panel on mobile via custom event
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("budb-open-wallet-sheet", onOpen);
    return () => window.removeEventListener("budb-open-wallet-sheet", onOpen);
  }, []);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock body scroll when drawer open on mobile
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      {/* Left edge tab — always visible */}
      <button
        type="button"
        className={`lobby-wallet-tab ${open ? "lobby-wallet-tab--open" : ""} ${
          sitReady ? "lobby-wallet-tab--ready" : ""
        }`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="lobby-wallet-panel"
        data-testid="button-wallet-tab"
      >
        <Wallet className="h-4 w-4 shrink-0" />
        <span className="lobby-wallet-tab__label">
          <span className="lobby-wallet-tab__title">Wallet</span>
          <span className="lobby-wallet-tab__bal tabular-nums">
            {formatPlayGbux(play, { compact: true })}
          </span>
        </span>
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Backdrop */}
      <div
        className={`lobby-wallet-backdrop ${open ? "lobby-wallet-backdrop--show" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden={!open}
      />

      {/* Slide panel */}
      <aside
        id="lobby-wallet-panel"
        className={`lobby-wallet-panel ${open ? "lobby-wallet-panel--open" : ""}`}
        aria-hidden={!open}
      >
        <div className="lobby-wallet-panel__inner">
          <LobbyWalletDash
            compact
            onClose={() => setOpen(false)}
            onReadySit={() => {
              onReadySit?.();
              setOpen(false);
            }}
          />
          <button
            type="button"
            className="lobby-wallet-panel__more text-[11px] text-cyan-400/80 w-full py-2"
            onClick={() => {
              setOpen(false);
              openWalletPanel("overview");
            }}
          >
            Full wallet drawer →
          </button>
        </div>
      </aside>
    </>
  );
}
